// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { createContext, useContext, useState, useEffect, useCallback, ReactNode, createElement } from 'react';
import { isNativePlatform } from '../utils/platform';

export interface NetworkStatus {
  isOnline: boolean;
  connectionType: string;
}

const NetworkStatusContext = createContext<NetworkStatus>({
  isOnline: true,
  connectionType: 'unknown',
});

/**
 * Provider that tracks network connectivity and exposes it via context.
 * Uses @capacitor/network on native platforms, navigator.onLine on web.
 */
export function NetworkStatusProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<NetworkStatus>(() => {
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    return {
      isOnline: online,
      connectionType: online ? 'unknown' : 'none',
    };
  });

  const handleOnline = useCallback(() => {
    setStatus({ isOnline: true, connectionType: 'unknown' });
  }, []);

  const handleOffline = useCallback(() => {
    setStatus({ isOnline: false, connectionType: 'none' });
  }, []);

  useEffect(() => {
    if (isNativePlatform()) {
      // Dynamic import for native — @capacitor/network
      let cleanup: (() => void) | undefined;

      import('@capacitor/network').then(({ Network }) => {
        // W97: this needs its own catch. The outer .catch below only covers the
        // dynamic import; a getStatus() that rejects — the plugin present but the
        // native call failing — became an unhandled rejection, and on a real device
        // that is an unhandled promise in the app, not just a noisy test. Vitest 1
        // swallowed it; Vitest 5 surfaced it. Keeping the initial state is the right
        // fallback: "we could not ask" is not the same as "offline", and telling a
        // member they are offline when they are not is the worse error.
        Network.getStatus()
          .then((s) => {
            setStatus({
              isOnline: s.connected,
              connectionType: s.connectionType,
            });
          })
          .catch(() => {
            // keep the initial state
          });

        const listenerHandle = Network.addListener('networkStatusChange', (s) => {
          setStatus({
            isOnline: s.connected,
            connectionType: s.connectionType,
          });
        });

        cleanup = () => {
          // Same reasoning: a listener that never resolved must not reject into
          // nothing when the component unmounts.
          listenerHandle.then((h) => h.remove()).catch(() => {});
        };
      }).catch(() => {
        // Fallback to web API if native plugin fails
      });

      return () => {
        cleanup?.();
      };
    }

    // Web fallback
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return createElement(NetworkStatusContext.Provider, { value: status }, children);
}

/**
 * React hook to access current network connectivity status.
 * Must be used within a NetworkStatusProvider.
 */
export function useNetworkStatus(): NetworkStatus {
  return useContext(NetworkStatusContext);
}
